<template>
  <Head v-if="inSSR">
    <component is="script" type="application/ld+json">
      {{ schemaData }}
    </component>
  </Head>
  <template v-else>
    <Teleport to="head">
      <component is="script" type="application/ld+json">
        {{ schemaData }}
      </component>
    </Teleport>
  </template>
</template>

<script setup>
import { usePage } from "@inertiajs/vue3";
import { computed, inject, onMounted } from "vue";
import { useCategoryStore } from "@/stores/category";
const page = usePage();
const settings = computed(() => page.props.setting ?? {});
const appLocale = computed(() => page.props.locale ?? {});
const __t = inject("__t");
const allLocale = computed(() => {
  return page.props.allLocale;
});

const inSSR = computed(() => typeof window == "undefined");
const props = defineProps({
  item: {
    type: Object,
    required: false,
  },
  category: {
    type: Object,
    required: false,
  },

  type: {
    type: String,
    required: false,
    default: "product",
  },
});
const categoryStore = useCategoryStore();
const categories = computed(() => categoryStore.categories);
onMounted(() => {
  if (categories.value?.length === 0) {
    categoryStore.fetchCategories();
  }
});
const getObjectCategories = computed(() => {
  return (
    categories.value?.map((category) => ({
      "@type": "SiteNavigationElement",
      name: category.title,
      url: route("category", category.slug),
    })) || []
  );
});
const schemaData = computed(() => {
  if (props.type == "product") {
    return JSON.stringify({
      "@context": "http://schema.org",
      "@type": "ClassifiedAd",
      name: props.item.title_current_lang,
      description: props.item.short_description_current_lang,
      datePosted: props.item.published_at_format,
      category: props.item.product_category.title,
   

  
     
    
      image:
        props.item.files
          ?.filter((file) =>
            [
              "image/jpeg",
              "image/png",
              "png",
              "webp",
              "jpeg",
              "jpg",
              "image",
            ].includes(file.mime_type)
          )
          ?.map(
            (file) => new URL(file.media_url, settings.value.site_url).href
          ) || [],
      ...(props.item.user && {
        seller: {
          "@type": "Person",
          name: props.item.user.name,
          ...(props.item.user.phone && { telephone: props.item.user.phone }),
        },
      }),
      ...(props.item.country && {
        location: {
          "@type": "Place",
          address: {
            "@type": "PostalAddress",
            addressCountry: props.item.country.name,
            ...(props.item.state?.name && {
              addressRegion: props.item.state.name,
            }),
            ...(props.item.city?.name && {
              addressLocality: props.item.city.name,
            }),
            ...(props.item.district && {
              streetAddress: props.item.district.name,
            }),
          },
        },
      }),
      ...(props.item.expired_at && { validThrough: props.item.expired_at }),
      url: props.item.url,
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: __t("home"),
            item: route("home"),
          },
          ...(props.item.product_category.parent
            ? [
                {
                  "@type": "ListItem",
                  position: 2,
                  name: props.item.product_category.parent.title,
                  item: props.item.product_category.parent.url,
                },
              ]
            : []),
          {
            "@type": "ListItem",
            position: props.item.product_category.parent ? 3 : 2,
            name: props.item.product_category.title,
            item: props.item.product_category.url,
          },
        ],
      },
    });
  } else if (props.type == "service") {
    return JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      name: props.item.title,
      description: props.item.description,
      provider: {
        "@type": "Person",
        name: props.item.user?.name || "",
        ...(props.item.user?.phone && { telephone: props.item.user.phone }),
        ...(props.item.user?.avatar && {
          image: new URL(props.item.user.avatar, settings.value.site_url).href,
        }),
      },
      serviceType: props.item.service_category?.title || "",
      areaServed: {
        "@type": "Place",
        name: props.item.country?.name || "",
        ...(props.item.state?.name && { address: props.item.state.name }),
        ...(props.item.city?.name && { address: props.item.city.name }),
      },

      image:
        props.item.files
          ?.filter((file) =>
            [
              "image/jpeg",
              "image/png",
              "png",
              "webp",
              "jpeg",
              "jpg",
              "image",
            ].includes(file.mime_type)
          )
          ?.map(
            (file) => new URL(file.media_url, settings.value.site_url).href
          ) || [],
      url: props.item.url,
      datePosted: props.item.published_at_format,

      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: __t("home"),
            item: route("home"),
          },
          ...(props.item.service_category?.parent
            ? [
                {
                  "@type": "ListItem",
                  position: 2,
                  name: props.item.service_category.parent.title,
                  item: props.item.service_category.parent.url,
                },
              ]
            : []),
          {
            "@type": "ListItem",
            position: props.item.service_category?.parent ? 3 : 2,
            name: props.item.service_category?.title || "",
            item: props.item.service_category?.url || "#",
          },
        ],
      },
    });
  } else if (props.type == "web") {
    return JSON.stringify([
      {
        "@context": "http://schema.org",
        "@type": "WebSite",
        name: settings.value.site_seo_title[appLocale.value],
        url: route("home"),
        potentialAction: {
          "@type": "SearchAction",
          target: route("home") + "?q={search_term_string}",
          "query-input": "required name=search_term_string",
        },
        description: settings.value.site_descr[appLocale.value],
      },
      {
        "@context": "http://schema.org",
        "@type": "ItemList",
        itemListElement: [
          {
            "@type": "SiteNavigationElement",
            name: __t("home"),
            url: route("home"),
          },
          ...getObjectCategories.value,
        ],
      },
    ]);
  } else if (props.type == "category") {
    return JSON.stringify({
      "@context": "https://schema.org",
      "@type": ["CollectionPage", "CategoryCode"],
      name: props.category.title_current_lang,
      description: props.category.description_current_lang,
      url: route("category", props.category.slug),
      ...(props.category.thumb_file && {
        image: props.category.thumb_file.media_url,
      }),
      categoryCode: props.category.slug,
      ...(props.category.parent && {
        parentCategory: {
          "@type": "CategoryCode",
          name: props.category.parent.title_current_lang,
          url: props.category.parent.url,
          categoryCode: props.category.parent.slug,
        },
      }),
      hasCategoryCode: props.category.children?.map((child) => ({
        "@type": "CategoryCode",
        name: child.title_current_lang,
        url: child.url,
        categoryCode: child.slug,
      })),

      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: __t("home"),
            item: route("home"),
          },
          ...(props.category.parent
            ? [
                {
                  "@type": "ListItem",
                  position: 2,
                  name: props.category.parent.title_current_lang,
                  item: props.category.parent.url,
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: props.category.title_current_lang,
                  item: route("category", props.category.slug),
                },
              ]
            : [
                {
                  "@type": "ListItem",
                  position: 2,
                  name: props.category.title_current_lang,
                  item: props.category.url,
                },
              ]),
        ],
      },
    });
  } else {
    return;
  }
});
</script>
